import { joinClassNames } from '@vertex-protocol/web-common';
import { Icons, Spinner } from '@vertex-protocol/web-ui';
import { NadoWithdrawButtonState } from '../types';

interface Props {
  state: NadoWithdrawButtonState;
  chainName?: string;
  bridgeProvider?: 'cctp' | 'oft' | 'across' | 'none';
  /** Bridge tx hash on Ink (source for withdrawals) */
  bridgeTxHash?: string;
  /** Source token withdrawn from Nado/Ink */
  tokenSymbol?: string;
  /** Bridge fee for display in the progress step hint */
  bridgeFee?: string;
}

type StepStatus = 'pending' | 'active' | 'complete';

interface Step {
  label: string;
  hint?: string;
  status: StepStatus;
  explorerUrl?: string;
}

/** Progress tracker for the withdrawal + optional Across bridge flow. */
export function NadoWithdrawProgress({ state, chainName = 'Chain', bridgeProvider = 'oft', bridgeTxHash, tokenSymbol = 'USDT0', bridgeFee }: Props) {
  const bridgeExplorerLink = bridgeTxHash
    ? `https://explorer.inkonchain.com/tx/${bridgeTxHash}`
    : undefined;
  const steps = getSteps(state, chainName, bridgeProvider, bridgeExplorerLink, bridgeFee, tokenSymbol);

  // Only show when the flow has started (past ready_withdraw)
  if (!shouldShowProgress(state)) return null;

  return (
    <div className="bg-surface-2 flex flex-col gap-y-3 rounded-sm p-3">
      {/* Progress bar */}
      <div className="flex gap-x-1">
        {steps.map((step, i) => (
          <div
            key={i}
            className={joinClassNames(
              'h-1 flex-1 rounded-full transition-all duration-500',
              step.status === 'complete'
                ? 'bg-positive'
                : step.status === 'active'
                  ? 'bg-primary'
                  : 'bg-surface-3',
            )}
          />
        ))}
      </div>

      {/* Step labels */}
      <div className="flex flex-col gap-y-1.5">
        {steps.map((step, i) => (
          <div key={i} className="flex items-center gap-x-2">
            <StepIcon status={step.status} />
            <div className="flex flex-col">
              {step.explorerUrl && step.status === 'complete' ? (
                <a
                  href={step.explorerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-positive text-xs underline decoration-positive/40 hover:decoration-positive transition-colors"
                >
                  {step.label} ↗
                </a>
              ) : (
                <span
                  className={joinClassNames(
                    'text-xs',
                    step.status === 'active'
                      ? 'text-text-primary font-medium'
                      : step.status === 'complete'
                        ? 'text-positive'
                        : 'text-text-tertiary',
                  )}
                >
                  {step.label}
                </span>
              )}
              {step.hint && step.status === 'active' && (
                <span className="text-text-tertiary text-2xs">
                  {step.hint}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepIcon({ status }: { status: StepStatus }) {
  if (status === 'complete') {
    return <Icons.CheckCircle className="text-positive size-3.5" />;
  }
  if (status === 'active') {
    return <Spinner className="text-primary size-3.5" />;
  }
  return <div className="bg-surface-3 size-3.5 rounded-full" />;
}

function shouldShowProgress(state: NadoWithdrawButtonState): boolean {
  return [
    'withdrawing',
    'requires_approve_oft',
    'approving_oft',
    'ready_bridge',
    'preparing_bridge',
    'bridging',
    'bridge_pending',
    'partial_arrival',
    'arrival_timeout',
    'success',
  ].includes(state);
}

function getSteps(state: NadoWithdrawButtonState, chainName: string, bridgeProvider: string, bridgeExplorerLink?: string, bridgeFee?: string, tokenSymbol = 'USDT0'): Step[] {
  const withdrawStatus = getWithdrawStatus(state);
  const approveStatus = getApproveStatus(state);
  const bridgeStatus = getBridgeStatus(state);

  const feeLabel = bridgeFee && bridgeFee !== '0' ? ` · Fee: ${bridgeFee}` : '';
  const bridgeHint = bridgeProvider === 'across'
    ? `~2 sec via Across${feeLabel}`
    : bridgeProvider === 'cctp'
      ? `~20–60 min via CCTP · Free`
      : `~1–3 min via LayerZero${feeLabel}`;

  if (bridgeProvider === 'none') {
    return [
      { label: `Withdraw ${tokenSymbol} to Ink wallet`, hint: '~5–30 sec', status: withdrawStatus },
    ];
  }

  if (bridgeProvider === 'across') {
    return [
      { label: `Withdraw ${tokenSymbol} to Ink wallet`, hint: '~5–30 sec', status: withdrawStatus },
      { label: `Bridge to ${chainName}`, hint: bridgeHint, status: bridgeStatus, explorerUrl: bridgeExplorerLink },
    ];
  }

  return [
    { label: `Withdraw ${tokenSymbol} to Ink wallet`, hint: '~5–30 sec', status: withdrawStatus },
    { label: 'Approve bridge transaction', hint: 'Requires wallet signature', status: approveStatus },
    { label: `Bridge to ${chainName}`, hint: bridgeHint, status: bridgeStatus, explorerUrl: bridgeExplorerLink },
  ];
}

function getWithdrawStatus(state: NadoWithdrawButtonState): StepStatus {
  if (state === 'withdrawing') return 'active';
  if ([
    'requires_approve_oft', 'approving_oft',
    'ready_bridge', 'preparing_bridge', 'bridging', 'bridge_pending',
    'partial_arrival', 'arrival_timeout', 'success',
  ].includes(state)) return 'complete';
  return 'pending';
}

function getApproveStatus(state: NadoWithdrawButtonState): StepStatus {
  if (state === 'requires_approve_oft' || state === 'approving_oft') return 'active';
  if ([
    'ready_bridge', 'preparing_bridge', 'bridging', 'bridge_pending',
    'partial_arrival', 'arrival_timeout', 'success',
  ].includes(state)) return 'complete';
  return 'pending';
}

function getBridgeStatus(state: NadoWithdrawButtonState): StepStatus {
  if (
    state === 'ready_bridge' ||
    state === 'preparing_bridge' ||
    state === 'bridging' ||
    state === 'bridge_pending' ||
    state === 'partial_arrival' ||
    state === 'arrival_timeout'
  ) return 'active';
  if (state === 'success') return 'complete';
  return 'pending';
}
